import { Box, Button, colors, MenuItem, Select } from '@mui/material'
import { EthAccountButton, FlexCol, FlexGrowCol, FlexGrowRow, FlexRow } from '@xylabs/sdk-react'
import { Quadkey } from '@xyo-network/sdk-xyoworld-js'
import { useContext, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'

import { GeoJsonMap, GeotokenIdentification } from '../../Components'
import { AuthenticationContext, XyoWorldAuthenticatedEntity } from '../../Contexts'
import { QuadkeyHeader } from '../../Toolbar'
import WorldBasePage from '../WorldBasePage'

type ActivityType = 'Event' | 'Chat' | 'BoundWitness'

const Activity: React.FC = () => {
  const [activityType, setActivityType] = useState<ActivityType>('BoundWitness')
  return (
    <FlexGrowCol justifyContent="flex-start" margin={1}>
      <Select<ActivityType> onChange={(event) => setActivityType(event.target.value as ActivityType)} value={activityType} variant="standard">
        <MenuItem value={'Event'}>Events</MenuItem>
        <MenuItem value={'Chat'}>Chat</MenuItem>
        <MenuItem value={'BoundWitness'}>Bound Witnesses</MenuItem>
      </Select>
    </FlexGrowCol>
  )
}

const PlayerSlots: React.FC = () => {
  return (
    <FlexCol>
      <FlexGrowRow>
        <Box border={1} borderRadius={5} height={48} margin={0.5} width={48} />
        <Box border={1} borderRadius={5} height={48} margin={0.5} width={48} />
        <Box border={1} borderRadius={5} height={48} margin={0.5} width={48} />
        <Box border={1} borderRadius={5} height={48} margin={0.5} width={48} />
      </FlexGrowRow>
    </FlexCol>
  )
}

const PlaceSlots: React.FC = () => {
  return (
    <FlexCol>
      <FlexGrowRow>
        <Box border={1} borderRadius={5} height={32} margin={0.5} width={32} />
        <Box border={1} borderRadius={5} height={32} margin={0.5} width={32} />
        <Box border={1} borderRadius={5} height={32} margin={0.5} width={32} />
        <Box border={1} borderRadius={5} height={32} margin={0.5} width={32} />
      </FlexGrowRow>
    </FlexCol>
  )
}

interface PlayerInfoProps {
  entity?: XyoWorldAuthenticatedEntity | null
}

const PlayerInfo: React.FC<PlayerInfoProps> = (props) => {
  const { entity } = props
  return entity ? (
    <FlexGrowCol alignItems="stretch" justifyContent="flex-start" padding={1}>
      <EthAccountButton variant="outlined" address={entity.address} />
      <PlayerSlots />
    </FlexGrowCol>
  ) : null
}

interface PlaceInfoProps {
  quadkey?: Quadkey
}

const PlaceInfo: React.FC<PlaceInfoProps> = (props) => {
  const { quadkey } = props
  return quadkey ? (
    <FlexCol alignItems="stretch" bgcolor={colors.blueGrey[500]} justifyContent="center" padding={1}>
      <FlexGrowRow>
        <GeotokenIdentification cropMap={false} icon={false} quadkey={quadkey} worldMap={false} />
        <PlaceSlots />
      </FlexGrowRow>
    </FlexCol>
  ) : null
}

const Controls: React.FC = () => {
  return (
    <FlexGrowRow margin={1}>
      <Box margin={0.5}>
        <Button variant="outlined">Live</Button>
      </Box>
      <Box margin={0.5}>
        <Button variant="outlined">Plan</Button>
      </Box>
      <Box margin={0.5}>
        <Button variant="outlined">History</Button>
      </Box>
    </FlexGrowRow>
  )
}

const PlacePage: React.FC = () => {
  const { id } = useParams()
  const quadkeyRequested = Quadkey.fromBase4String(id) ?? Quadkey.root
  const { authEntity } = useContext(AuthenticationContext)

  return (
    <WorldBasePage>
      <Helmet title="Place" />
      <QuadkeyHeader quadkey={quadkeyRequested} />
      <FlexRow alignItems="stretch">
        <FlexRow paper bgcolor={colors.blueGrey[500]} margin={1} width={256}>
          <Activity />
        </FlexRow>
        <FlexCol alignItems="stretch" width={512}>
          <FlexCol margin={1}>
            <FlexRow paper bgcolor={colors.blueGrey[500]} flexDirection="column">
              <Controls />
              <GeoJsonMap quadkey={quadkeyRequested} />
              <PlaceInfo quadkey={quadkeyRequested} />
            </FlexRow>
          </FlexCol>
        </FlexCol>
        <FlexRow paper bgcolor={colors.blueGrey[500]} margin={1} width={256}>
          <PlayerInfo entity={authEntity} />
        </FlexRow>
      </FlexRow>
    </WorldBasePage>
  )
}

export default PlacePage
